import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import formServices from 'services/form-builder-services'
import { dlTrackEvent } from 'components/dataLayer/dataLayer'
import toast from 'react-hot-toast'
import textServices from 'services/text-services'
import { Checkbox, DivCheckbox, Label, PCheckbox } from 'theme/theme'
import InputReferral from 'components/input-referral'
import useLocalStorage from 'hooks/use-local-storage'
import { useEffect } from 'react'

interface IProps {
  isSocial?: boolean
  onSubmit: (data: { referral: string, termsOfUse: boolean, privacyPolicy: boolean, subscribe: boolean }) => void
}

const ComponentInput = ({ field: { onChange, value } }) => {
  return (
    <InputReferral
      setQR={(code: string) => onChange(code)}
      onBlur={(e) => {
        dlTrackEvent('form_interaction', {
          form_field: 'referral',
          form_name: 'datos cuenta',
          form_action: e.target.value ? 'complete' : 'empty',
          section: 'sign up'
        })
      }}
      onChange={onChange}
      value={value}
    />
  )
}

const ReferralCodeV2 = ({ isSocial, onSubmit }: IProps) => {
  const [referral] = useLocalStorage<string>('referralCode')
  const { handleSubmit, setValue, control, watch, formState: { errors } } = useForm<{ referral: string, termsOfUse: boolean, privacyPolicy: boolean, subscribe: boolean }>({
    resolver: yupResolver(formServices.formGenerateSchema(isSocial ? ['referral', 'termsOfUse', 'privacyPolicy'] : ['referral'])),
    defaultValues: { termsOfUse: false, privacyPolicy: false, subscribe: false },
    criteriaMode: 'firstError',
    reValidateMode: 'onSubmit',
    mode: 'all'
  })
  const termsOfUse = watch('termsOfUse')
  const privacyPolicy = watch('privacyPolicy')
  const suscribe = watch('subscribe')

  const onInvalid = (errors: Record<string, any>) => {
    if (errors) {
      const errorsKeys = Object.values(errors)
      toast.error(errorsKeys[0]?.message, { className: 'toast__error' })
    }
  };

  const handleTermsOfUse = () => {
    dlTrackEvent('user_interaction', {
      action: 'select',
      element: 'Acepto terminos y condiciones',
      section: 'sign up'
    })

    gtag('event', 'Click', {
      event_category: GTM_EVENT_CATEGORY,
      event_label: 'MODIFICAR INFORMACION - Acepto terminos y condiciones',
      value: '10'
    })
    setValue('termsOfUse', !termsOfUse)
  }

  const handlePrivacyPolicy = () => {
    dlTrackEvent('user_interaction', {
      action: 'select',
      element: 'Acepto condiciones de privacidad',
      section: 'sign up'
    })

    gtag('event', 'Click', {
      event_category: GTM_EVENT_CATEGORY,
      event_label: 'MODIFICAR INFORMACION - Acepto condiciones de privacidad',
      value: '10'
    })
    setValue('privacyPolicy', !privacyPolicy)
  }

  const handleSubscribe = () => {
    dlTrackEvent('user_interaction', {
      action: 'select',
      element: 'Acepto recibir novedades',
      section: 'sign up'
    })
    gtag('event', 'Click', {
      event_category: GTM_EVENT_CATEGORY,
      event_label: 'MODIFICAR INFORMACION - Acepto recibir novedades',
      value: '10'
    })
    setValue('subscribe', !suscribe)
  }

  useEffect(() => {
    console.log(referral)
    if (referral) {
      setValue('referral', referral)
    }
  }, [referral])

  return (
    <div className="pb-4">
      <h2 className="form__sign-in__title" style={{ marginBottom: '16px' }}>Código referido</h2>
      <p className='text big text-center mb-4'>¿Tienes un código de referido? Es el momento de usarlo. Si no tienes uno no te preocupes, puedes completar el registro omitiendo este paso.</p>

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className='space-y-4'>
        <div className={`gap-2 flex flex-col input-container ${errors?.referral?.message ? 'error-input' : ''}`}>
          <Label className="text-start text-2xl font-bold uppercase input-label">
            {textServices.findByText('profile/form/information/referral/label', {
              es: 'CÓDIGO REFERIDO (OPCIONAL)',
              en: 'REFERRAL CODE (OPTIONAL)'
            })}
          </Label>
          <Controller
            name='referral'
            control={control}
            render={ComponentInput}
          />
        </div>
        {
          isSocial && (
            <div className='space-y-2'>
              <DivCheckbox className="gap-4 flex items-center cursor-pointer input-container" onClick={handleTermsOfUse}>
                <Checkbox active={termsOfUse} className={`h-6 w-6 flex items-center justify-center`} id="termsOfUse" />
                <PCheckbox className="dinPro input-checkbox-label flex-1">
                  {textServices.findByText('signup/termsOfUse/label', {
                    es: (
                      <>
                        Acepto los{' '}
                        <a
                          href={`${process.env.PUBLIC_URL}/terms-and-conditions`}
                          rel="noreferrer"
                          target="_blank"
                          className="underline"
                        >
                          Términos y Condiciones
                        </a>
                        .
                      </>
                    ),
                    en: (
                      <>
                        I accept the{' '}
                        <a
                          href={`${process.env.PUBLIC_URL}/terms-and-conditions`}
                          rel="noreferrer"
                          target="_blank"
                          className="underline"
                        >
                          Terms and Conditions
                        </a>
                        .
                      </>
                    )
                  })}
                </PCheckbox>
              </DivCheckbox>
              <DivCheckbox
                className="gap-4 flex items-center cursor-pointer input-container"
                onClick={handlePrivacyPolicy}
              >
                <Checkbox
                  active={privacyPolicy}
                  className={`h-6 w-6 flex items-center justify-center`}
                  id="privacyPolicy"
                />
                <PCheckbox className="dinPro input-checkbox-label flex-1">
                  {textServices.findByText('signup/privacyPolicy/label', {
                    es: (
                      <>
                        Acepto el{' '}
                        <a
                          href={`${process.env.PUBLIC_URL}/privacy-politics`}
                          rel="noreferrer"
                          target="_blank"
                          className="underline"
                        >
                          Aviso de privacidad
                        </a>
                        .
                      </>
                    ),
                    en: (
                      <>
                        I accept the{' '}
                        <a
                          href={`${process.env.PUBLIC_URL}/privacy-politics`}
                          rel="noreferrer"
                          target="_blank"
                          className="underline"
                        >
                          Privacy Policy
                        </a>
                        .
                      </>
                    )
                  })}
                </PCheckbox>
              </DivCheckbox>
              <DivCheckbox className="gap-4 flex items-center cursor-pointer input-container" onClick={handleSubscribe}>
                <Checkbox className={`h-6 w-6 flex items-center justify-center`} id="suscribe" active={suscribe} />
                <PCheckbox className="dinPro input-checkbox-label flex-1">
                  {textServices.findByText('signup/suscribe/label', {
                    es: (
                      <>
                        Tu privacidad es nuestra prioridad. Al &quot;Aceptar&quot;, nos das tu consentimiento para procesar
                        tus datos personales con el fin de mejorar tu experiencia y ofrecerte contenido personalizado. En
                        cualquier momento, puedes gestionar o retirar tu consentimiento accediendo a tu <b>Preference Center</b> o
                        a través de los medios que ponemos a tu disposición en el{' '}
                        <a
                          href={`${process.env.PUBLIC_URL}/privacy-politics`}
                          rel="noreferrer"
                          target="_blank"
                          className="underline"
                        >
                          Aviso de Privacidad
                        </a>
                        .
                      </>
                    ),
                    en: (
                      <>
                        Your privacy is our priority. By &quot;Accepting&quot;, you give us your consent to process your
                        personal data in order to improve your experience and offer you personalized content. At any time,
                        you can manage or withdraw your consent by accessing your <b>Preference Center</b> or through the means we
                        make available to you in the {' '}
                        <a
                          href={`${process.env.PUBLIC_URL}/privacy-politics`}
                          rel="noreferrer"
                          target="_blank"
                          className="underline"
                        >
                          Privacy Notice
                        </a>.
                      </>
                    )
                  })}
                </PCheckbox>
              </DivCheckbox>
            </div>
          )
        }

        <button type="submit" className="form__sign-in__btn form__sign-in__btn__main">
          {isSocial ? 'Siguiente' : 'Registrarme'}
        </button>
      </form>
    </div>
  )
}

export default ReferralCodeV2
